// Advanced Table: the Helios Advanced Table on a <table> a server already rendered —
// expandable nested rows, row selection with a select-all, a sticky header, resizable
// columns, sortable columns and a tooltip or a visually hidden label on a header cell.
//
// The Ember component builds the table out of <div role="row">s because its CSS uses
// grid and `display: grid` drops the table semantics a browser derives from the tags.
// Here the author's real <table> is kept and only styled, and the roles the grid would
// have taken away are written back onto the real elements, so the markup stays server
// renderable and the semantics survive.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";
import css from "./advanced-table.css";

const ALIGN = ["left", "center", "right"];
const SORT_ICON = { asc: "arrow-up", desc: "arrow-down" };
const HIDDEN_SEGMENT = "i80-advanced-table__th-button-aria-label-hidden-segment";
// The header cell's inner row, as I80::Layout::Flex renders it for the Ember component.
const FLEX = "i80-layout-flex i80-layout-flex--direction-row i80-layout-flex--justify-content-space-between"
  + " i80-layout-flex--align-items-center i80-layout-flex--row-gap-8 i80-layout-flex--column-gap-8";
const LABEL = "i80-advanced-table__th-content-text i80-typography-body-200 i80-font-weight-semibold";
const MIN_WIDTH = 48;
const RESIZE_STEP = 10;

/** One rule components.css leaves to the page; see advanced-table.css. */
function ensureStyles() {
  if (document.querySelector("style[data-i80-advanced-table]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-i80-advanced-table", "");
  style.textContent = css;
  document.head.append(style);
}

/**
 * <i80-advanced-table caption="Policy sets" selectable select-name="policy">
 *   <table>
 *     <thead><tr><th expandable>Name</th><th sort>Status</th></tr></thead>
 *     <tbody>
 *       <tr expanded value="1"><th>Policy set 1</th><td>Pass</td></tr>
 *       <tr depth="1"><th>test-advisory.sentinel</th><td>Pass</td></tr>
 *     </tbody>
 *   </table>
 * </i80-advanced-table>
 *
 * The <table> is the one you (or your server) wrote. A <tr depth="1"> is a child of the
 * row above it and is hidden until that row is expanded; <th sort> gets a sort button;
 * selectable adds a checkbox column with a select-all that goes indeterminate.
 */
export class I80AdvancedTable extends I80Element {
  static get observedAttributes() {
    return ["density", "valign", "striped", "caption", "selectable", "select-name", "select-all-label",
      "sticky-header", "max-height", "resizable-columns", "sort-mode", "sort-selected"];
  }

  setup() {
    ensureStyles();
    this._uid = nextId("adv-table");
    this.adopt();
  }

  /** The table may be written after the tag (template engines, streamed HTML). */
  adopt() {
    const table = this.querySelector("table");
    if (!table) return;

    let container = this.part("container");
    if (!container) {
      container = h("div", { class: "i80-advanced-table__container", "data-i80-part": "container" });
      this.append(container);
    }
    if (table.parentElement !== container) container.append(table);
    this._container = container;

    // An empty state the author wrote: <i80-application-state slot="empty">…
    // It lives outside the table, so it is taken out of the DOM while there are rows.
    let empty = this.part("empty") ?? this._empty ?? null;
    const own = [...this.children, ...container.children].find((el) => el.getAttribute?.("slot") === "empty");
    if (own) {
      empty ??= h("div", { class: "i80-advanced-table__empty-state", "data-i80-part": "empty" });
      if (own.parentElement !== empty) empty.append(own);
    }
    this._empty = empty;

    if (table === this._table) return;
    this._table = table;
    this._widths = null;

    container.addEventListener("click", (e) => {
      const button = e.target.closest("button[data-i80-part]");
      if (!button || !container.contains(button)) return;
      if (button.dataset.i80Part === "sort") this._sort(button.closest("th, td"));
      if (button.dataset.i80Part === "expand") this._expand(button);
    });
    container.addEventListener("change", (e) => {
      const box = e.target.closest('input[type="checkbox"][data-i80-part]');
      if (!box) return;
      if (box.dataset.i80Part === "select-all") this.selectAll(box.checked);
      else this._selectionChanged(box);
    });
    container.addEventListener("pointerdown", (e) => {
      const handle = e.target.closest('[data-i80-part="resize"]');
      if (handle && e.button === 0) this._beginResize(handle, e);
    });
    container.addEventListener("keydown", (e) => {
      const handle = e.target.closest('[data-i80-part="resize"]');
      if (!handle) return;
      const step = e.key === "ArrowLeft" ? -RESIZE_STEP : e.key === "ArrowRight" ? RESIZE_STEP : 0;
      if (!step) return;
      e.preventDefault();
      const columns = this._columns();
      const index = columns.indexOf(handle.closest("th"));
      this._measure(columns);
      this._resizeTo(index, parseFloat(this._widths[index]) + step);
    });
  }

  // --- reading the markup -------------------------------------------------

  /** The header cells the author wrote, without the selection column we add. */
  _columns() {
    const row = this._table?.tHead?.rows[0];
    return row ? [...row.cells].filter((c) => c.dataset.i80Part !== "select-all") : [];
  }

  /** The author's cells of a body row, without the selection cell we add. */
  _cells(row) {
    return [...row.cells].filter((c) => c.dataset.i80Part !== "select");
  }

  /**
   * The body rows with their place in the tree: depth comes from the row's own
   * `depth`, a row is a parent when the row after it is deeper, and a row is visible
   * when every ancestor is expanded.
   */
  _rows() {
    const rows = [];
    for (const body of this._table.tBodies) {
      for (const tr of body.rows) rows.push({ tr, depth: Math.max(0, parseInt(tr.getAttribute("depth") ?? "0", 10) || 0) });
    }
    const open = [];
    rows.forEach((row, i) => {
      row.hasChildren = (rows[i + 1]?.depth ?? 0) > row.depth;
      row.expanded = row.tr.hasAttribute("expanded") && row.tr.getAttribute("expanded") !== "false";
      row.visible = row.depth === 0 ? true : open[row.depth - 1] === true;
      open[row.depth] = row.visible && row.expanded;
    });
    return rows;
  }

  get _selectionBoxes() {
    return [...this._table.querySelectorAll('tbody input[data-i80-part="select"]')];
  }

  /** The <tr>s whose checkbox is ticked. */
  get selectedRows() {
    return this._selectionBoxes.filter((b) => b.checked).map((b) => b.closest("tr"));
  }

  /** The `value` of every ticked row, i.e. what a form would submit. */
  get selectedValues() {
    return this._selectionBoxes.filter((b) => b.checked).map((b) => b.value);
  }

  // --- rendering ----------------------------------------------------------

  update() {
    const table = this._table;
    if (!table) return;
    const rows = this._rows();
    const columns = this._columns();
    const nested = rows.some((r) => r.hasChildren);
    // Helios allows neither selection nor stripes together with nested rows: the
    // checkbox column has no meaning on a parent row, and the stripes fight the
    // parent/child shading.
    const selectable = this.flag("selectable") && !nested;
    const maxHeight = this.getAttribute("max-height");
    const sticky = maxHeight ? this.getAttribute("sticky-header") !== "false" : false;
    if (!maxHeight && this.flag("sticky-header") && !this._warned) {
      this._warned = true;
      console.warn("<i80-advanced-table> sticky-header needs max-height: the header can only stick while the body scrolls.");
    }
    const resizable = this.flag("resizable-columns");

    table.className = [
      "i80-advanced-table",
      this.flag("striped") && !nested && "i80-advanced-table--striped",
      `i80-advanced-table--density-${this.choice("density", ["default", "short", "medium", "tall"], "medium")}`,
      `i80-advanced-table--valign-${this.choice("valign", ["baseline", "middle", "top"], "top")}`,
      nested && "i80-advanced-table--nested",
    ].filter(Boolean).join(" ");
    table.setAttribute("role", "table");
    table.style.maxHeight = maxHeight ?? "";
    if (this._widths && this._widths.length !== columns.length) this._widths = null;
    table.style.gridTemplateColumns = [
      selectable && "min-content",
      ...columns.map((cell, i) => this._widths?.[i] ?? cell.getAttribute("width") ?? "auto"),
    ].filter(Boolean).join(" ");

    const caption = this.getAttribute("caption");
    const own = table.querySelector(":scope > caption");
    if (caption || own) {
      const el = own ?? table.insertBefore(h("caption"), table.firstChild);
      el.className = "sr-only i80-advanced-table__caption";
      if (caption) el.textContent = caption;
    }

    if (table.tHead) {
      table.tHead.className = ["i80-advanced-table__thead", sticky && "i80-advanced-table__thead--sticky",
        resizable && "i80-advanced-table__thead--has-resizable-columns"].filter(Boolean).join(" ");
      table.tHead.setAttribute("role", "rowgroup");
      for (const row of table.tHead.rows) {
        row.className = "i80-advanced-table__tr";
        row.setAttribute("role", "row");
      }
    }
    for (const body of table.tBodies) {
      body.className = "i80-advanced-table__tbody";
      body.setAttribute("role", "rowgroup");
    }

    // The column that carries the expand button: the one marked expandable, else the first.
    const expandIndex = nested ? Math.max(0, columns.findIndex((c) => c.hasAttribute("expandable"))) : -1;
    const allExpanded = rows.some((r) => r.hasChildren) && rows.filter((r) => r.hasChildren).every((r) => r.expanded);

    this._selectAllCell(selectable);
    columns.forEach((cell, i) => this._head(cell, i, {
      expandAll: i === expandIndex && !cell.hasAttribute("sort"),
      allExpanded,
      resize: resizable && i < columns.length - 1,
    }));

    const last = rows.filter((r) => r.visible).pop();
    rows.forEach((row, i) => this._row(row, i, { last: row === last, selectable, nested, expandIndex }));
    this._syncSelectAll();
    this._emptyState(rows.length === 0);
    this._resizeHeights(resizable);
  }

  // --- the header ---------------------------------------------------------

  _selectAllCell(selectable) {
    const row = this._table.tHead?.rows[0];
    if (!row) return;
    let cell = row.querySelector(':scope > [data-i80-part="select-all"]');
    if (!selectable) {
      cell?.remove();
      return;
    }
    if (!cell) {
      cell = h("th", { "data-i80-part": "select-all", scope: "col" },
        h("div", { class: "i80-advanced-table__th-content" },
          h("input", { type: "checkbox", class: "i80-form-checkbox i80-advanced-table__checkbox", "data-i80-part": "select-all" })));
      row.insertBefore(cell, row.firstChild);
    }
    cell.className = "i80-advanced-table__th i80-advanced-table__th--is-selectable";
    cell.setAttribute("role", "columnheader");
    const box = cell.querySelector('input[data-i80-part="select-all"]');
    box.setAttribute("aria-label", this.getAttribute("select-all-label") ?? "Select all rows");

    const order = this._order(cell);
    if (this.flag("sort-selected")) {
      cell.setAttribute("aria-sort", order ? `${order}ending` : "none");
      if (!cell.hasAttribute("sort")) cell.setAttribute("sort", "");
      const label = this._label(cell, "sel", box.getAttribute("aria-label"));
      this._sortButton(cell.firstElementChild, order, label.id, "sel");
    } else {
      cell.removeAttribute("aria-sort");
      cell.removeAttribute("sort");
      cell.querySelector('[data-i80-part="sort"]')?.remove();
    }
  }

  /** A visually hidden label the sort button can point at with aria-labelledby. */
  _label(cell, key, text) {
    let label = cell.querySelector(`:scope > .i80-advanced-table__th-content > .sr-only`);
    if (!label) {
      label = h("span", { class: "sr-only" }, text);
      cell.firstElementChild.append(label);
    }
    label.id = `${this._uid}-${key}`;
    return label;
  }

  _order(cell) {
    return ["asc", "desc"].includes(cell.getAttribute("sort")) ? cell.getAttribute("sort") : null;
  }

  _head(cell, index, { expandAll, allExpanded, resize }) {
    const align = ALIGN.includes(cell.getAttribute("align")) ? cell.getAttribute("align") : null;
    const sortable = cell.hasAttribute("sort");
    const hiddenLabel = cell.hasAttribute("hidden-label");
    cell.className = ["i80-advanced-table__th", sortable && "i80-advanced-table__th--sort",
      align && `i80-advanced-table__th--align-${align}`].filter(Boolean).join(" ");
    cell.setAttribute("role", "columnheader");
    if (!cell.getAttribute("scope")) cell.setAttribute("scope", "col");
    this._span(cell);

    // The cell's own text is wrapped once; on later updates the wrapper is already there.
    let flex = cell.querySelector(":scope > .i80-layout-flex");
    if (!flex) {
      const inner = hiddenLabel
        ? h("span", { class: "sr-only" }, ...cell.childNodes)
        : h("div", { class: "i80-advanced-table__th-content" }, h("span", { class: LABEL }, ...cell.childNodes));
      flex = h("div", { class: FLEX }, inner);
      cell.replaceChildren(flex);
    }
    flex.className = FLEX;
    const label = flex.querySelector(`.${hiddenLabel ? "sr-only" : "i80-advanced-table__th-content-text"}`);
    if (label) label.id = `${this._uid}-l${index}`;

    if (sortable) {
      const order = this._order(cell);
      cell.setAttribute("aria-sort", order ? `${order}ending` : "none");
      this._sortButton(flex, order, label?.id, index);
    } else {
      cell.removeAttribute("aria-sort");
      flex.querySelector(':scope > [data-i80-part="sort"]')?.remove();
    }

    if (expandAll) this._expandButton(flex, { expanded: allExpanded, all: true, labelId: label?.id, index });
    else flex.querySelector(':scope > [data-i80-part="expand"]')?.remove();

    const tip = cell.getAttribute("tooltip");
    const content = flex.querySelector(":scope > .i80-advanced-table__th-content");
    if (tip && content) this._tooltipButton(content, tip, label?.id, index);
    else content?.querySelector(':scope > i80-tooltip')?.remove();

    this._resizeHandle(cell, resize, label?.textContent?.trim() ?? "", index);
  }

  /**
   * The sort button, always the last child of the header cell's inner row. Its label is
   * "Sort by <column> ascending": three ids, so a screen reader reads what a click does.
   */
  _sortButton(parent, order, labelId, key) {
    const prefix = `${this._uid}-sp${key}`;
    const suffix = `${this._uid}-ss${key}`;
    let button = parent.querySelector(':scope > [data-i80-part="sort"]');
    if (!button) {
      button = h("button", { type: "button", "data-i80-part": "sort" },
        h("span", { class: HIDDEN_SEGMENT }, "Sort by"),
        h("span", { class: HIDDEN_SEGMENT }));
      parent.append(button);
    }
    button.children[0].id = prefix;
    button.children[1].id = suffix;
    button.children[1].textContent = order === "asc" ? "descending" : "ascending";
    button.setAttribute("aria-labelledby", [prefix, labelId, suffix].filter(Boolean).join(" "));
    button.className = ["i80-advanced-table__th-button", "i80-advanced-table__th-button--sort",
      order && "i80-advanced-table__th-button--is-sorted"].filter(Boolean).join(" ");
    button.querySelector("svg")?.remove();
    button.append(icon(SORT_ICON[order] ?? "swap-vertical"));
  }

  /** The expand button: first child of a header cell's flex row, and of a parent row's cell. */
  _expandButton(parent, { expanded, all, labelId, index }) {
    let button = parent.querySelector(':scope > [data-i80-part="expand"]');
    if (!button) {
      button = h("button", { type: "button", "data-i80-part": "expand",
        "aria-description": "Toggle the visibility of the related rows." },
        h("span", { class: HIDDEN_SEGMENT, id: `${this._uid}-ep${index}` }, "Toggle"));
      parent.insertBefore(button, parent.firstChild);
    }
    button.firstElementChild.id = `${this._uid}-ep${index}`;
    button.setAttribute("aria-labelledby", `${this._uid}-ep${index} ${labelId ?? ""}`.trim());
    button.setAttribute("aria-expanded", String(expanded));
    button.className = ["i80-advanced-table__th-button", "i80-advanced-table__th-button--expand",
      expanded && "i80-advanced-table__th-button--is-expanded"].filter(Boolean).join(" ");
    button.querySelector("svg")?.remove();
    button.append(icon(all ? (expanded ? "unfold-close" : "unfold-open") : (expanded ? "chevron-up" : "chevron-down")));
  }

  _tooltipButton(content, text, labelId, index) {
    let wrapper = content.querySelector(":scope > i80-tooltip");
    if (!wrapper) {
      const prefix = `${this._uid}-tp${index}`;
      wrapper = h("i80-tooltip", { placement: "bottom" },
        h("button", { type: "button", class: "i80-advanced-table__th-button i80-advanced-table__th-button--tooltip" },
          h("span", { class: HIDDEN_SEGMENT, id: prefix }, "More information for"), icon("info")));
      content.append(wrapper);
    }
    wrapper.setAttribute("text", text);
    const button = wrapper.querySelector("button");
    button.setAttribute("aria-labelledby", `${this._uid}-tp${index} ${labelId ?? ""}`.trim());
  }

  _resizeHandle(cell, wanted, columnLabel, index) {
    let handle = cell.querySelector(':scope > [data-i80-part="resize"]');
    if (!wanted) {
      handle?.remove();
      return;
    }
    if (!handle) {
      handle = h("div", { class: "i80-advanced-table__th-resize-handle", "data-i80-part": "resize",
        role: "slider", tabindex: "0", draggable: "false", "aria-orientation": "horizontal" });
      cell.append(handle);
    }
    const width = Math.round(parseFloat(this._widths?.[index] ?? "") || cell.getBoundingClientRect().width);
    handle.setAttribute("aria-label", `Resize ${columnLabel} column`);
    handle.setAttribute("aria-valuemin", String(this._min(cell)));
    handle.setAttribute("aria-valuemax", String(Number.isFinite(this._max(cell)) ? this._max(cell) : width * 4));
    handle.setAttribute("aria-valuenow", String(width));
    handle.setAttribute("aria-valuetext", `${width}px`);
  }

  /** The handles reach down the whole table, as they do in the Ember component. */
  _resizeHeights(resizable) {
    if (!resizable) return;
    const height = this._table.scrollHeight;
    for (const handle of this._table.querySelectorAll('thead [data-i80-part="resize"]')) {
      if (height) handle.style.height = `${height}px`;
    }
  }

  // --- the body -----------------------------------------------------------

  _row(row, index, { last, selectable, nested, expandIndex }) {
    const { tr, depth, hasChildren, expanded, visible } = row;
    const box = this._selectCell(tr, selectable, index);
    const selected = box ? box.checked : tr.hasAttribute("selected");
    tr.className = [
      "i80-advanced-table__tr",
      depth > 0 && "i80-advanced-table__tr--nested",
      hasChildren && "i80-advanced-table__tr--parent-row",
      !visible && "i80-advanced-table__tr--hidden",
      last && "i80-advanced-table__tr--last-row",
      selected && "i80-advanced-table__tr--is-selected",
    ].filter(Boolean).join(" ");
    tr.setAttribute("role", "row");

    this._cells(tr).forEach((cell, i) => {
      const head = cell.localName === "th";
      const align = ALIGN.includes(cell.getAttribute("align")) ? cell.getAttribute("align") : null;
      cell.className = head
        ? ["i80-advanced-table__th", align && `i80-advanced-table__th--align-${align}`].filter(Boolean).join(" ")
        : ["i80-advanced-table__td", align && `i80-advanced-table__td--align-${align}`].filter(Boolean).join(" ");
      cell.setAttribute("role", head ? "rowheader" : "cell");
      if (head && !cell.getAttribute("scope")) cell.setAttribute("scope", "row");
      this._span(cell);
      cell.style.paddingLeft = nested && i === 0 ? `calc(${depth} * 32px + 16px)` : "";

      // A row header always gets the Ember component's inner row; a plain cell only
      // when it has to carry the expand button.
      const needsWrapper = head || (i === expandIndex && hasChildren);
      let flex = cell.querySelector(":scope > .i80-layout-flex");
      if (needsWrapper && !flex) {
        flex = h("div", { class: FLEX }, h("div", { class: "i80-advanced-table__th-content" },
          h("span", { class: LABEL }, ...cell.childNodes)));
        cell.replaceChildren(flex);
      }
      if (!flex) return;
      const label = flex.querySelector(".i80-advanced-table__th-content-text");
      if (label) label.id = `${this._uid}-r${index}`;
      if (i === expandIndex && hasChildren) {
        this._expandButton(flex, { expanded, all: false, labelId: label?.id, index: `r${index}` });
      } else {
        flex.querySelector(':scope > [data-i80-part="expand"]')?.remove();
      }
    });
  }

  _selectCell(tr, selectable, index) {
    let cell = tr.querySelector(':scope > [data-i80-part="select"]');
    if (!selectable) {
      cell?.remove();
      return null;
    }
    if (!cell) {
      cell = h("td", { "data-i80-part": "select" },
        h("div", { class: "i80-advanced-table__th-content" },
          h("input", { type: "checkbox", class: "i80-form-checkbox i80-advanced-table__checkbox",
            "data-i80-part": "select", checked: tr.hasAttribute("selected") || null })));
      tr.insertBefore(cell, tr.firstChild);
    }
    // The Ember component reuses its Th for this cell, so the class is the header one.
    cell.className = "i80-advanced-table__th i80-advanced-table__th--is-selectable";
    cell.setAttribute("role", "cell");
    const box = cell.querySelector('input[data-i80-part="select"]');
    const name = this.getAttribute("select-name");
    if (name) box.setAttribute("name", name);
    else box.removeAttribute("name");
    const value = tr.getAttribute("value");
    if (value !== null) box.value = value;
    box.setAttribute("aria-label", `Select ${tr.getAttribute("select-label") ?? `row ${index + 1}`}`);
    return box;
  }

  /** colspan and rowspan have to become grid spans: the table lays out as a grid. */
  _span(cell) {
    for (const [attr, prop, aria] of [["colspan", "gridColumn", "aria-colspan"], ["rowspan", "gridRow", "aria-rowspan"]]) {
      const n = parseInt(cell.getAttribute(attr) ?? "", 10);
      cell.style[prop] = n > 1 ? `span ${n}` : "";
      if (n > 1) cell.setAttribute(aria, String(n));
      else cell.removeAttribute(aria);
    }
  }

  _emptyState(empty) {
    if (!empty) {
      this._empty?.remove();
      return;
    }
    if (!this._empty) {
      this._empty = h("div", { class: "i80-advanced-table__empty-state", "data-i80-part": "empty" },
        h("i80-application-state", { title: "No data available", align: "center" },
          h("p", {}, "There is currently no data to display in the table.")));
    }
    if (this._empty.parentElement !== this._container) this._container.append(this._empty);
  }

  // --- behaviour ----------------------------------------------------------

  /** Expand or collapse one row, by its <tr> or its index among the body rows. */
  toggleRow(row, expanded) {
    const tr = typeof row === "number" ? this._rows()[row]?.tr : row;
    if (!tr) return;
    const next = expanded ?? !(tr.hasAttribute("expanded") && tr.getAttribute("expanded") !== "false");
    if (next) tr.setAttribute("expanded", "");
    else tr.removeAttribute("expanded");
    this.update();
    this.dispatchEvent(new CustomEvent("i80-expand", { bubbles: true, detail: { row: tr, expanded: next, all: false } }));
  }

  expandAll() {
    this._setAllExpanded(true);
  }

  collapseAll() {
    this._setAllExpanded(false);
  }

  _setAllExpanded(expanded) {
    for (const row of this._rows()) {
      if (!row.hasChildren) continue;
      if (expanded) row.tr.setAttribute("expanded", "");
      else row.tr.removeAttribute("expanded");
    }
    this.update();
    this.dispatchEvent(new CustomEvent("i80-expand", { bubbles: true, detail: { row: null, expanded, all: true } }));
  }

  _expand(button) {
    if (button.closest("thead")) {
      this._setAllExpanded(button.getAttribute("aria-expanded") !== "true");
      return;
    }
    this.toggleRow(button.closest("tr"));
  }

  /** Tick or untick every row, as the header checkbox does. */
  selectAll(checked) {
    for (const box of this._selectionBoxes) box.checked = checked;
    this.update();
    this.dispatchEvent(new CustomEvent("i80-select", {
      bubbles: true,
      detail: { key: "all", all: true, checked, selected: this.selectedValues },
    }));
  }

  _selectionChanged(box) {
    this.update();
    this.dispatchEvent(new CustomEvent("i80-select", {
      bubbles: true,
      detail: { key: box.value, all: false, checked: box.checked, selected: this.selectedValues },
    }));
  }

  /** The select-all box follows the rows: ticked, empty, or indeterminate in between. */
  _syncSelectAll() {
    const all = this._table.querySelector('thead input[data-i80-part="select-all"]');
    if (!all) return;
    const boxes = this._selectionBoxes;
    const checked = boxes.filter((b) => b.checked).length;
    all.checked = boxes.length > 0 && checked === boxes.length;
    all.indeterminate = checked > 0 && checked < boxes.length;
  }

  _sort(cell) {
    const head = this._table.tHead?.rows[0];
    if (!cell || !head) return;
    const order = cell.getAttribute("sort") === "asc" ? "desc" : "asc";
    for (const other of head.querySelectorAll("[sort]")) {
      if (other !== cell) other.setAttribute("sort", "");
    }
    cell.setAttribute("sort", order);
    const selection = cell.dataset.i80Part === "select-all";
    const index = selection ? -1 : this._columns().indexOf(cell);
    const key = selection ? "selected"
      : cell.getAttribute("sort-key") ?? cell.querySelector(".i80-advanced-table__th-content-text")?.textContent.trim() ?? "";

    if (this.getAttribute("sort-mode") !== "server") this.sortRows(index, order);
    this.update();
    this.dispatchEvent(new CustomEvent("i80-sort", { bubbles: true, detail: { key, order, index } }));
  }

  /**
   * Sort the body rows by one column, index -1 meaning "selected rows first". A parent
   * row moves with its children: the rows of one tree stay together.
   */
  sortRows(index, order) {
    for (const body of this._table.tBodies) {
      const groups = [];
      for (const tr of [...body.rows]) {
        const depth = Math.max(0, parseInt(tr.getAttribute("depth") ?? "0", 10) || 0);
        if (depth === 0 || !groups.length) groups.push([tr]);
        else groups[groups.length - 1].push(tr);
      }
      const value = (group) => {
        if (index === -1) return group[0].querySelector('input[data-i80-part="select"]')?.checked ? "1" : "0";
        const cell = this._cells(group[0])[index];
        return cell?.getAttribute("sort-value")
          ?? cell?.querySelector(".i80-advanced-table__th-content-text")?.textContent.trim()
          ?? cell?.textContent.trim() ?? "";
      };
      const number = (group) => Number(value(group).replace(/[\s,]/g, ""));
      const numeric = groups.every((group) => value(group) === "" || !Number.isNaN(number(group)));
      const compare = numeric
        ? (a, b) => (number(a) || 0) - (number(b) || 0)
        : (a, b) => value(a).localeCompare(value(b), undefined, { numeric: true, sensitivity: "base" });
      groups.sort((a, b) => (order === "asc" ? compare(a, b) : compare(b, a)));
      body.append(...groups.flat());
    }
  }

  // --- column resizing ----------------------------------------------------

  _min(cell) {
    return Math.max(1, parseFloat(cell.getAttribute("min-width") ?? "") || MIN_WIDTH);
  }

  _max(cell) {
    return parseFloat(cell.getAttribute("max-width") ?? "") || Infinity;
  }

  /** Freeze every column at the width it has now, so one of them can be changed. */
  _measure(columns) {
    if (this._widths?.length === columns.length) return;
    this._widths = columns.map((c) => `${Math.round(c.getBoundingClientRect().width)}px`);
  }

  _beginResize(handle, event) {
    const columns = this._columns();
    const index = columns.indexOf(handle.closest("th"));
    if (index < 0) return;
    event.preventDefault();
    this._measure(columns);
    const startX = event.clientX;
    const startWidth = parseFloat(this._widths[index]);
    handle.classList.add("i80-advanced-table__th-resize-handle--resizing");
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      // A synthetic event without a live pointer: the document listeners below still work.
    }
    const move = (e) => this._resizeTo(index, startWidth + (e.clientX - startX));
    const stop = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", stop);
      handle.removeEventListener("pointercancel", stop);
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", stop);
      handle.classList.remove("i80-advanced-table__th-resize-handle--resizing");
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", stop);
    handle.addEventListener("pointercancel", stop);
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", stop);
  }

  _resizeTo(index, width) {
    const columns = this._columns();
    const cell = columns[index];
    if (!cell) return;
    this._measure(columns);
    const next = Math.round(Math.min(this._max(cell), Math.max(this._min(cell), width)));
    if (this._widths[index] === `${next}px`) return;
    this._widths[index] = `${next}px`;
    this.update();
    this.dispatchEvent(new CustomEvent("i80-resize", {
      bubbles: true,
      detail: { index, width: next, widths: [...this._widths] },
    }));
  }
}

define("i80-advanced-table", I80AdvancedTable);
